import { Component, OnDestroy, OnInit, computed } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FirestoreService } from '../../services/firestore.service';
import { Router } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { SubscriptionService } from '../../services/subscription.service';
import { SubscriptionPlansService } from '../../services/subscription-plans.service';
import { SubscriptionPlan } from '../../models/subscription-plan.model';
import { Subject, of, switchMap, takeUntil, catchError } from 'rxjs';

@Component({
  selector: 'app-subscriptions',
  templateUrl: './subscriptions.component.html',
  styleUrls: ['./subscriptions.component.css']
})
export class SubscriptionsComponent implements OnInit, OnDestroy {
  readonly plans = toSignal(this.subscriptionPlansService.getPublishedPlansWithFallback(), { initialValue: [] });
  readonly hasPlans = computed(() => this.plans().length > 0);
  userId: string = '';
  subscriptionId: string | null = null;
  isLoggedIn: boolean = false;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly firestoreService: FirestoreService,
    private readonly router: Router,
    private readonly authService: AuthService,
    private readonly subscriptionService: SubscriptionService,
    private readonly subscriptionPlansService: SubscriptionPlansService
  ){}

  ngOnInit(): void {
    this.authService.user$
      .pipe(
        switchMap(user => {
          this.isLoggedIn = !!user;
          if (!user) {
            this.userId = '';
            this.subscriptionId = null;
            return of(null);
          }
          this.userId = user.uid;
          return this.firestoreService.getUserSubscriptionId(user.uid).pipe(
            catchError(err => {
              console.error('Failed to get user subscriptionId:', err);
              return of(null);
            })
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(subscriptionId => {
        this.subscriptionId = subscriptionId;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  selectPlanAndNavigate(plan: SubscriptionPlan) {
    this.subscriptionService.setSelectedPlan(plan.level);

    if (!this.isLoggedIn) {
      this.router.navigate(['/register']);
      return;
    }

    const subscription = {
      userId: this.userId,
      planId: plan.level,
      status: '',
      paymentId: null,
      error: null,
      createdAt: '',
      updatedAt: ''
    };

    if (this.subscriptionId) {
      this.updateSubscription(this.subscriptionId, subscription, plan.level);
    } else {
      this.recordPlanSelection(subscription, plan.level);
    }
  }

  recordPlanSelection(plan: any, selectedPlanLevel: string) {
    this.firestoreService.recordPlanSelection(plan).subscribe({
      next: (subscriptionId: any) => {
        this.updateUserSubscriptionId(this.userId, subscriptionId, selectedPlanLevel);
      },
      error: (err: any) => {
        console.error('Failed to record plan selection:', err);
      }
    });
  }

  updateUserSubscriptionId(userId: string, subscriptionId: string, selectedPlanLevel: string) {
    this.firestoreService.updateUserSubscriptionId(userId, subscriptionId).subscribe({
        next: () => {
          this.router.navigate(['/checkout'], { state: { selectedPlanLevel, subscriptionId } });
        },
        error: (err) => {
          console.error('Failed to update user subscriptionId:', err);
        }
      });
  }

  updateSubscription(subscriptionId: string, plan: any, selectedPlanLevel: string): void {
    this.firestoreService.updateSubscription(subscriptionId, plan).subscribe({
      next: () => {
        this.router.navigate(['/checkout'], { state: { selectedPlanLevel, subscriptionId } });
      },
      error: (err) => {
        console.error('Failed to update subscription:', err);
      }
    });
  }
}
